import { type Modification } from 'src/services/modification/types';
import { type AccommodationStatus } from 'src/store/placementDetailsPageSlice/types';
import { type Site } from 'src/store/siteSlice';
import { type IdentifyBodyLocationEnum } from 'src/utils/jmrUtils';

import { type Task } from '../tasks/types';

export interface DownloadTWPAPayload {
  accommodationId: number;
  reportDate: string;
  department: string;
  notes: string;
  jobModifications: string;
  workPlacementProvide: string;
  isFullDuty: boolean;
  placementShift: string;
}

export interface DownloadOomdPayload {
  accommodationId: number;
  injury: string;
  reportDate: string;
  reportDateToAMCARE: string;
  additionalNotes: string;
}

export enum YesNoEnum {
  Yes = 'Yes',
  No = 'No',
}

export interface JmrPayload {
  location: string;
  siteId: number;
  shiftLength: number;
  jobLevel: string | null;
  taskRestrictions: {
    taskId: number;
    measureUnit: string;
    restrictedValue: number | YesNoEnum; // number when measureUnit is hour and YesNoEnum when measureUnit is yes/no
  }[];
}

export interface JmrWorkRestriction {
  taskId: number;
  hoursRestricted: string;
  taskName: string;
  task?: Task;
  bodyLocation?: IdentifyBodyLocationEnum;
}

export interface AllocatedJobShift {
  jobId: number;
  shiftId?: number;
  jobName: string;
  shiftName: undefined | string;
  allocatedHours: number;
  appliedModifications?: AppliedModification[];
}

export interface AccommodationFields {
  associateFullName: string;
  requesterLogin: string;
  startAt: string;
  endAt: string;
  managerFullName: string;
  managerAlias: string;
  employeeId: string;
  personId: string;
  // We don't allow users to set this value so there is a chance it is still null from PAPI and we can't do much except
  // pass it along. Is not used for any logic in the backend, it is a record-keeping and display concern
  job?: string | null;
  login: string;
  fc: string;
  siteId: number;
  accommodationType: string;
  externalCaseId: string;
  weeklyLimitedHours: number;
  modifiedShiftLength: number;
  normalShiftLength: number;
  canWorkNormalShiftHours: boolean;
  canWorkMoreThan40Hours: boolean;
  shiftCode: string;
  jobLevel: string | null;
  workRestrictions: JmrWorkRestriction[];
  allocatedJobShifts: AllocatedJobShift[];
}

export interface Accommodation extends AccommodationFields {
  accommodationStatusId: number;
  accommodationStatus: AccommodationStatus;
  caseIdValid: boolean;
  site: Site;
  previousSite: Site | null;
}

export interface UpdateAccommodationFields extends AccommodationFields {
  id: number;
  accommodationStatus: {
    id: number;
    status: string;
  };
}

export interface AccommodationPayload {
  accommodation: AccommodationFields;
  emailRecipients: string;
}

export interface UpdateAccommodationPayload extends AccommodationPayload {
  accommodation: UpdateAccommodationFields;
}

export interface UpdateAssociateInfoPayload {
  accommodationId: number;
  login: string;
  job: string;
  employeeId: string;
  personId: string;
  shiftCode: string;
  externalCaseId: string;
  caseSource: string;
  fullName: string;
}

export interface TaskRestriction {
  taskId: number;
  originalValue: number;
  restrictedValue: number;
  taskName: string;
}

export interface AppliedModification {
  taskId: number;
  modificationId: number;
  freeFormText?: string;
}

export interface NamedAppliedModification extends AppliedModification {
  // this is a frontend only property that is used in the placementSelectionReview component
  name: string;
}

export interface TaskConflict {
  taskId: number;
  requirementValue: string;
  /** Number between 0 and 1 representing proportion of the shift the task is required to be done
   * NOTE: this is the JobRequirement.value column in the DB which will always be string containing a number
   * (even for YES/NO tasks)
   */
  requirementProportion: string;
  restrictedValue: string;
  taskName: string;
  measureUnitType: string;
  availableModifications: Modification[];
  appliedModifications?: NamedAppliedModification[];
}
